import { Card } from '../../atoms/card/card.js';
import { Sparkline } from '../../atoms/sparkline/sparkline.js';

/**
 * StatTile — one labelled figure from the case queue, with an optional trend.
 *
 * Corpus: `DB:477-482` (the app KPI tile — a figure over a
 * `#6e6e73` label on a 16px-radius tile), with that figure's 28px/700/-0.02em
 * treatment at `DB:1888-1891`. `PR1:131-140`
 * is the landing page's larger cousin. Both stack the value above the label and
 * this tile does not; the inversion predates the move into tiers and is left
 * alone, because a move is not the place to change what ships.
 *
 * The trend has no corpus provenance — no file in `the corpus` draws a sparkline.
 * It comes from the dataviz spec, which is why it is optional rather than
 * assumed: a metric with no history is the normal case, not a broken tile.
 *
 * Label, value and trend description are all props: every string a person reads
 * is supplied by the caller from a translation catalogue.
 *
 * Where the trend sits is this tile's layout rather than Sparkline's, so the
 * `margin-block-start: auto` that pins it to the floor of the flex column is
 * declared on a wrapper this file owns. Sparkline itself carries no placement:
 * an atom named after the molecule it happened to appear in first would follow
 * that molecule's margin into every other layout it is ever used in.
 */
export interface StatTileProps {
  readonly label: string;
  readonly value: string;
  readonly trend?: readonly number[];
  readonly trendLabel?: string;
}

export function StatTile({ label, value, trend, trendLabel }: StatTileProps) {
  return (
    <Card className="rp-stat-tile">
      <span className="rp-stat-tile__label">{label}</span>
      <span className="rp-stat-tile__value">{value}</span>
      {trend && trend.length >= 2 ? (
        <span className="rp-stat-tile__spark">
          <Sparkline points={trend} label={trendLabel ?? label} />
        </span>
      ) : null}
    </Card>
  );
}
